import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { Lock, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mandatory password rotation page. Reached when the backend sends
 * `must_change_password: true` on the login response — happens for
 * Owner-invited users on first login and for any user whose Owner
 * triggered an admin reset.
 *
 * The page is a hard gate: until the form succeeds, the user can
 * neither navigate elsewhere nor see any tenant data. We use the
 * existing `/api/auth/users/change-password` endpoint (which already
 * unsets `must_change_password` server-side on success).
 */
export default function ForcePasswordChange() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // If a user somehow lands here without the flag, send them home —
  // and likewise force-redirect anonymous browsers to login.
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
    } else if (user && user.must_change_password === false) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const validate = () => {
    if (newPassword.length < 8) return "La nueva contraseña debe tener al menos 8 caracteres.";
    if (newPassword === currentPassword) return "La nueva contraseña debe ser distinta de la temporal.";
    if (newPassword !== confirmPassword) return "Las contraseñas no coinciden.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/auth/users/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true },
      );
      // Refresh the AuthContext so the must_change_password flag is
      // clear before we redirect — otherwise the dashboard guard would
      // bounce us right back here.
      await checkAuth();
      toast.success("Contraseña actualizada. ¡Bienvenido!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      // FastAPI 422 returns `detail` as an array of validation errors
      // — coerce so React can render it without crashing.
      const raw = err.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map((e) => e.msg || JSON.stringify(e)).join(" · ")
        : (typeof raw === "string" ? raw : "No fue posible cambiar la contraseña.");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4" data-testid="force-password-change-page">
      <div className="w-full max-w-md">
        <div className="bg-slate-900/80 backdrop-blur border border-slate-800 rounded-2xl shadow-2xl p-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 mb-5 mx-auto">
            <Lock size={20} className="text-amber-400" />
          </div>
          <h1 className="text-xl font-semibold text-white text-center mb-2">Cambia tu contraseña temporal</h1>
          <p className="text-sm text-slate-400 text-center leading-relaxed mb-6">
            Por seguridad, debes elegir una nueva contraseña antes de continuar.
          </p>

          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 mb-6 text-xs text-amber-200 flex gap-2">
            <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
            <p>Tu contraseña actual fue generada automáticamente y enviada por email. Cambiarla es obligatorio.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs text-slate-300">Contraseña temporal actual</Label>
              <div className="relative mt-1">
                <Input
                  type={show ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoFocus
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  tabIndex={-1}
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-300">Nueva contraseña</Label>
              <Input
                type={show ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="Al menos 8 caracteres"
                data-testid="new-password-input"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-300">Confirmar nueva contraseña</Label>
              <Input
                type={show ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="confirm-password-input"
              />
            </div>

            {error && (
              <p className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded px-3 py-2" data-testid="force-pwd-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-teal-500 hover:bg-teal-600 text-slate-950 font-medium"
              data-testid="force-pwd-submit-btn"
            >
              {submitting ? "Cambiando..." : "Cambiar contraseña y continuar"}
            </Button>
          </form>
        </div>

        <p className="text-xs text-slate-600 text-center mt-4">
          ¿Problemas? Contacta al Owner de tu organización.
        </p>
      </div>
    </div>
  );
}
